// app/shared/components/notifications/HTFNotificationDisplay.tsx
"use client";

import {
  AlertTriangle,
  CheckCircle,
  Info as InfoIcon,
  Link as LinkIcon,
  X,
  XCircle as AlertXCircle,
} from "lucide-react"; // Renamed XCircle to AlertXCircle to avoid conflict
import Link from "next/link";
import React, { JSX } from "react";

import {
  HTFBaseCard,
  HTFCardVariant,
} from "@/app/shared/components/cards/HTFBaseCard"; // Import HTFBaseCard
import {
  ToastVariant,
  useNotificationStore,
} from "@/app/shared/store/notificationsStore";
import { cn } from "@/lib/utils";

// SingleToastProps - link is already there
interface SingleToastProps {
  id: string; // Add id to props if we need to pass it to HTFBaseCard for any reason (e.g., key, though map handles it)
  message: string;
  variant?: ToastVariant;
  title?: string;
  onClose: () => void;
  link?: string;
}

// SingleToast component - Reworked to use HTFBaseCard or similar principles
function SingleToast({
  id,
  message,
  variant = "default",
  title,
  onClose,
  link,
}: SingleToastProps) {
  const getVariantConfig = (): {
    cardVariant: HTFCardVariant;
    icon: JSX.Element;
    accentColor?: string;
  } => {
    switch (variant) {
      case "success":
        return {
          cardVariant: "success",
          icon: <CheckCircle className="h-6 w-6 text-success" />, // Main icon color from variant
          accentColor: "bg-success",
        };
      case "error":
        return {
          cardVariant: "error",
          icon: <AlertXCircle className="h-6 w-6 text-error" />,
          accentColor: "bg-error",
        };
      case "warning":
        return {
          cardVariant: "warning",
          icon: <AlertTriangle className="h-6 w-6 text-warning" />,
          accentColor: "bg-warning",
        };
      case "info":
        return {
          cardVariant: "info",
          icon: <InfoIcon className="h-6 w-6 text-info" />,
          accentColor: "bg-info",
        };
      default: // 'default' variant
        return {
          cardVariant: "default",
          icon: <InfoIcon className="h-6 w-6 text-gray-700" />, // Default icon color
          accentColor: "bg-gray-600",
        };
    }
  };

  const { cardVariant, icon, accentColor } = getVariantConfig();

  // Use HTFBaseCard for the toast structure
  // We'll make it compact and control padding more directly
  return (
    <div className="w-full max-w-md pointer-events-auto">
      {" "}
      {/* Container for the card to manage width */}
      <HTFBaseCard
        variant={cardVariant}
        className="shadow-2xl !border-gray-300" // Stronger shadow, consistent border
        accentBar={true} // Use the accent bar from HTFBaseCard
        compact={true} // Use compact mode for tighter spacing
        onClick={link ? () => {} : undefined} // Make card clickable if link exists, but actual navigation is via Link
      >
        <div className="flex items-start space-x-3">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            {React.cloneElement(icon, {
              className: cn(icon.props.className, "h-5 w-5"),
            })}
          </div>

          {/* Content */}
          <div className="flex-grow">
            {title && (
              <h3 className="text-md font-semibold text-gray-900">{title}</h3>
            )}
            <p className="text-sm text-gray-700 mt-0.5">{message}</p>
            {link && (
              <Link
                href={link}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent card's onClick if it were also navigating
                  // Optionally, close the toast on link click: onClose();
                }}
                className="mt-2 text-sm font-medium text-primary hover:underline flex items-center gap-1"
              >
                View Details <LinkIcon size={14} />
              </Link>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent card's onClick
              onClose();
            }}
            className={cn(
              "ml-auto -mr-1 -mt-1 p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1",
              "text-gray-500 hover:text-gray-800 focus:ring-gray-500",
              // Make hover background match the card's accent lightly for better UX
              variant === "success" && "hover:bg-success/10 focus:ring-success",
              variant === "error" && "hover:bg-error/10 focus:ring-error",
              variant === "warning" && "hover:bg-warning/10 focus:ring-warning",
              variant === "info" && "hover:bg-info/10 focus:ring-info",
              variant === "default" &&
                "hover:bg-gray-400/10 focus:ring-gray-600",
            )}
            aria-label="Dismiss"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </HTFBaseCard>
    </div>
  );
}

// HTFNotificationDisplay: This component reads from the store and displays toasts
export function HTFNotificationDisplay() {
  const notifications = useNotificationStore((state) => state.notifications);
  const dismissNotification = useNotificationStore(
    (state) => state.dismissNotification,
  );

  // REMOVE useEffect for auto-dismissal
  // useEffect(() => {
  //   notifications.forEach(toast => {
  //     if (toast.duration) { // Only if duration is set
  //       const timer = setTimeout(() => {
  //         dismissNotification(toast.id);
  //       }, toast.duration);
  //       return () => clearTimeout(timer);
  //     }
  //   });
  // }, [notifications, dismissNotification]);

  if (notifications.length === 0) {
    return null;
  }

  return (
    // Positioned at bottom-left
    <div className="fixed bottom-6 left-6 z-[200] flex flex-col items-start gap-3 w-auto pointer-events-none">
      {notifications.map((toast) => (
        <div
          key={toast.id}
          // Animation: slide in from bottom, fade in
          className="animate-in slide-in-from-bottom fade-in-0 duration-300"
        >
          <SingleToast
            id={toast.id}
            message={toast.message}
            variant={toast.type}
            title={toast.title}
            link={toast.link}
            onClose={() => dismissNotification(toast.id)}
          />
        </div>
      ))}
    </div>
  );
}

// // app/shared/components/notifications/HTFNotificationDisplay.tsx
// 'use client';

// import React, { useEffect } from 'react';
// import { useNotificationStore, ToastVariant } from '@/app/shared/store/notificationsStore'; // Make sure ToastVariant is exported
// import { cn } from '@/lib/utils';
// import { XCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';
// import Link from 'next/link'; // For clickable links in toast

// // Interface for SingleToastProps now includes link
// interface SingleToastProps {
//   message: string;
//   variant?: ToastVariant;
//   title?: string;
//   onClose: () => void;
//   link?: string;
// }

// // SingleToast component - This is what displays each individual notification
// function SingleToast({
//   message,
//   variant = 'default',
//   title,
//   onClose,
//   link
// }: SingleToastProps) {

//   // ### CODE CHANGES START ###
//   // Color and Icon configurations to match your app's light theme
//   const getVariantConfig = () => {
//     switch (variant) {
//       case "success":
//         return {
//           styles: "bg-green-50 border-green-400 text-green-700",
//           icon: <CheckCircle className="text-green-500 h-5 w-5" />
//         };
//       case "error":
//         return {
//           styles: "bg-red-50 border-red-400 text-red-700",
//           icon: <XCircle className="text-red-500 h-5 w-5" />
//         };
//       case "warning":
//         return {
//           styles: "bg-yellow-50 border-yellow-400 text-yellow-700",
//           icon: <AlertTriangle className="text-yellow-500 h-5 w-5" />
//         };
//       case "info":
//         return {
//           styles: "bg-blue-50 border-blue-400 text-blue-700",
//           icon: <Info className="text-blue-500 h-5 w-5" />
//         };
//       default: // 'default' variant
//         return {
//           styles: "bg-surface-light border-gray-300 text-gray-700",
//           icon: <Info className="text-gray-500 h-5 w-5" />
//         };
//     }
//   };

//   const { styles, icon } = getVariantConfig();

//   return (
//     <div
//       className={cn(
//         "rounded-lg border p-4 shadow-lg w-full max-w-sm flex items-start space-x-3",
//         styles
//       )}
//     >
//       <div className="flex-shrink-0 mt-0.5">
//         {icon}
//       </div>
//       <div className="flex-grow">
//         {title && <h3 className="text-sm font-semibold currentcolor">{title}</h3>}
//         <p className="text-sm currentcolor">{message}</p>
//         {link && (
//           <Link href={link} legacyBehavior>
//             <a
//               className="mt-1 text-sm font-medium hover:underline text-primary"
//             >
//               View Details →
//             </a>
//           </Link>
//         )}
//       </div>
//       <button
//           onClick={onClose}
//           className={cn(
//               "ml-auto -mr-1 -mt-1 p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2",
//               "text-current opacity-70 hover:opacity-100 focus:ring-current focus:ring-offset-1"
//           )}
//           aria-label="Dismiss"
//       >
//         <XCircle className="h-5 w-5" />
//       </button>
//     </div>
//   );
// }

// // HTFNotificationDisplay: This component reads from the store and displays toasts
// export function HTFNotificationDisplay() {
//     const notifications = useNotificationStore((state) => state.notifications);
//     const dismissNotification = useNotificationStore((state) => state.dismissNotification);

//     useEffect(() => {
//       notifications.forEach(toast => { // toast here is of type AppNotification from the store
//         if (toast.duration) {
//           const timer = setTimeout(() => {
//             dismissNotification(toast.id);
//           }, toast.duration);
//           return () => clearTimeout(timer);
//         }
//       });
//     }, [notifications, dismissNotification]);

//     if (notifications.length === 0) {
//       return null;
//     }

//     return (
//       <div className="fixed top-6 right-6 z-[200] flex flex-col items-end gap-3 w-auto pointer-events-none">
//         {notifications.map((toast) => ( // toast here is of type AppNotification
//           <div
//             key={toast.id}
//             className="animate-in slide-in-from-top fade-in-0 duration-300 pointer-events-auto"
//           >
//             <SingleToast
//               message={toast.message}
//               variant={toast.type} // Use toast.type which is ToastVariant
//               title={toast.title}
//               link={toast.link}
//               onClose={() => dismissNotification(toast.id)}
//             />
//           </div>
//         ))}
//       </div>
//     );
//   }
